import React from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import {
  EVENT_REMINDER_OPTIONS,
  eventReminderLabel,
} from "../../constants/eventReminderOptions";
import { composerOutlinedFieldSx } from "./composerBorderStyles";

type EventReminderModesFieldProps = {
  value: string[];
  onChange: (modes: string[]) => void;
  helperText?: string;
  /** Compact layout for Event Settings cards (no label, small select). */
  compact?: boolean;
  /** Full-width field inside an expandable settings panel (no outer margin). */
  embedded?: boolean;
  /** Shown when nothing is picked. */
  emptyText?: string;
  disabled?: boolean;
  /** Needed when the page shows more than one, e.g. a row per Event. */
  id?: string;
  /** Names the field when its label is a separate heading above it; there's no "Remind members" label inside then. */
  ariaLabel?: string;
};

const EventReminderModesField: React.FC<EventReminderModesFieldProps> = ({
  value,
  onChange,
  helperText = "",
  compact = false,
  embedded = false,
  emptyText = "No reminders",
  disabled = false,
  id = "event-reminder-modes",
  ariaLabel,
}) => {
  const labelId = `${id}-label`;
  const showLabel = !compact && !ariaLabel;
  const selected = value ?? [];

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const next = event.target.value;
    onChange(
      typeof next === "string" ? next.split(",").filter(Boolean) : next
    );
  };

  return (
    <FormControl
      fullWidth
      sx={[
        composerOutlinedFieldSx,
        compact || embedded ? { m: 0 } : { mb: 2 },
      ]}
      size={compact ? "small" : "medium"}
    >
      {showLabel ? (
        <InputLabel id={labelId}>Remind members</InputLabel>
      ) : null}
      <Select
        labelId={showLabel ? labelId : undefined}
        inputProps={ariaLabel ? { "aria-label": ariaLabel } : undefined}
        id={id}
        multiple
        disabled={disabled}
        value={selected}
        onChange={handleChange}
        displayEmpty
        input={showLabel ? <OutlinedInput label="Remind members" /> : undefined}
        sx={
          compact
            ? {
                minWidth: { xs: 132, sm: 168 },
                "& .MuiSelect-select": {
                  py: 0.85,
                  fontSize: "0.8125rem",
                },
              }
            : undefined
        }
        renderValue={(selectedValues) => {
          const modes = selectedValues as string[];
          if (modes.length === 0) {
            return (
              <Typography variant="body2" color="text.secondary">
                {emptyText}
              </Typography>
            );
          }
          return (
            <Box
              component="span"
              sx={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {modes.map(eventReminderLabel).filter(Boolean).join(", ")}
            </Box>
          );
        }}
        MenuProps={{
          PaperProps: {
            style: { maxHeight: 320 },
          },
        }}
      >
        {EVENT_REMINDER_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox
              checked={selected.includes(option.value)}
              color="primary"
              sx={{ py: 0.25, mr: 0.5 }}
            />
            <ListItemText
              primary={option.label}
              primaryTypographyProps={{ variant: "body2" }}
            />
          </MenuItem>
        ))}
      </Select>
      {!compact && helperText ? (
        <FormHelperText>{helperText}</FormHelperText>
      ) : null}
    </FormControl>
  );
};

export default EventReminderModesField;
